import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import {
  faPenToSquare,
  faBox,
  faDroplet,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTheme } from "styled-components";
import { printPdfFromBlob } from "../../utils/printUtils";
import { OrderedCellRendererProps } from "../Grid/types";
import { MobilePhlebotomyIcon } from "../../assets/iconComponents/MobilePhleobotmyIcon";
import { PrintIcon } from "../../assets/iconComponents/PrintIcon";
import { DnaIcon } from "../../assets/iconComponents/DnaIcon";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";
import {
  NewReqActionTooltip,
  NewReqNameAndActionsText,
  NewReqNameStatusIcon,
  NewReqNameWithStatus,
} from "./NewReqNameAndActionsCell.styles";
import { OrderedRowActionBar } from "./OrderedRowActionBar";
import {
  OrderedRowAction,
  ORDERED_ROW_MENU_OPEN_CLASS,
  partitionOrderedRowActions,
} from "./orderedRowActions";
import {
  getOnlineOrderPrintout,
  updatePhlebotomyStatus,
} from "./Services/NewReqService";
import ShipKitToPatientModal from "./ShipKitToPatientModal";
import { SendActionType } from "../shared/Services/SharedServices";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { NewRequisitionType } from "./types";
import { EDIT_TOOLTIP_COPY, getEditTooltipState } from "./orderEdit";

export const isOrderPhlebotomyRequested = (
  order: Pick<NewRequisitionType, "is_mobile_phlebotomy">,
): boolean => Boolean(order.is_mobile_phlebotomy);

export const NameAndActionsCell: React.FC<OrderedCellRendererProps> = ({
  value,
  data,
  node,
  actionBarEnabled = true,
  setShowPhlebotomySnackbar,
  onEditOrder,
  editOrderEnabled = false,
}) => {
  const theme = useTheme();
  const [isPhlebotomyRequested, setIsPhlebotomyRequested] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [shipKitModalOpen, setShipKitModalOpen] = useState(false);
  const [overflowAnchor, setOverflowAnchor] =
    useState<HTMLButtonElement | null>(null);

  const { auth, setLoggedOut } = useAuthenticationHook();
  const { physicalDeviceType } = useDeviceHook();
  const { enabled: shipKitEnabled } = useBooleanFeatureFlag(
    FeatureFlags.ShipKitToPatient,
  );

  const submitPrint = async () => {
    if (auth) {
      setIsPrintLoading(true);
      try {
        const pdfUrl = await getOnlineOrderPrintout(
          auth.token,
          data.unity_complete_id,
          setLoggedOut,
        );
        if (pdfUrl) {
          await SendActionType(
            "ProviderOnlineOrderPrintTable",
            setLoggedOut,
            auth?.token,
          );
          const response = await fetch(pdfUrl, {
            method: "GET",
            headers: {
              "Content-Type": "application/pdf",
            },
          });
          const blob = await response.blob();
          const filename = `${data.first_name.slice(0, 1)}${data.last_name.slice(0, 1)}_ordered_report.pdf`;

          await printPdfFromBlob(blob, filename, physicalDeviceType);
          setIsPrintLoading(false);
        }
      } catch (error) {
        setIsPrintLoading(false);
      }
    }
  };

  const submitPhlebotomy = async () => {
    if (auth) {
      setIsPhlebotomyRequested(true);
      try {
        const success = await updatePhlebotomyStatus(
          data.unity_complete_id,
          auth.token,
          setLoggedOut,
        );
        if (success) {
          const patientName = `${data.first_name} ${data.last_name}`;
          setShowPhlebotomySnackbar?.({
            open: true,
            patientName,
          });
        } else {
          setIsPhlebotomyRequested(false);
        }
      } catch (error) {
        setIsPhlebotomyRequested(false);
      }
    }
  };

  const isPhlebotomyDisabled =
    isOrderPhlebotomyRequested(data) || isPhlebotomyRequested;
  const isShipKitDisabled = Boolean(data.is_ship_to_patient);
  const canRequestPhlebotomy =
    data.is_mobile_phlebotomy_account && !data.is_cfc_order;
  const canShipKitToPatient = shipKitEnabled && !data.is_cfc_order;
  const editTooltipState = getEditTooltipState(data, editOrderEnabled);
  const canEditOrder = editTooltipState !== "closed";
  const rowActions: OrderedRowAction[] = [];

  if (editOrderEnabled) {
    rowActions.push({
      key: "edit",
      label: EDIT_TOOLTIP_COPY[editTooltipState],
      disabled: !canEditOrder,
      icon: <FontAwesomeIcon icon={faPenToSquare} />,
      onSelect: () => onEditOrder?.(data),
      keepFocusWhenDisabled: true,
      tooltipBackgroundColor:
        editTooltipState === "urgent" ? theme.redDark : theme.greyDark,
    });
  }

  if (canRequestPhlebotomy) {
    rowActions.push({
      key: "phlebotomy",
      label: isPhlebotomyDisabled
        ? "Phlebotomy Requested"
        : "Request Phlebotomy",
      disabled: isPhlebotomyDisabled,
      icon: (
        <FontAwesomeIcon
          icon={faDroplet}
          fontSize={14}
          color={theme.redMedium}
        />
      ),
      onSelect: submitPhlebotomy,
    });
  }

  if (canShipKitToPatient) {
    rowActions.push({
      key: "shipKit",
      label: isShipKitDisabled
        ? "Kit Shipped to Patient"
        : "Ship Kit to Patient",
      disabled: isShipKitDisabled,
      icon: (
        <FontAwesomeIcon icon={faBox} fontSize={16} color={theme.redMedium} />
      ),
      onSelect: () => setShipKitModalOpen(true),
    });
  }

  rowActions.push({
    key: "print",
    label: "Print",
    disabled: isPrintLoading,
    icon: isPrintLoading ? (
      <DnaIcon color={theme.redMedium} />
    ) : (
      <PrintIcon width={14} height={14} />
    ),
    onSelect: submitPrint,
  });

  const { directActions, overflowActions, hasOverflow } =
    partitionOrderedRowActions(rowActions);

  // AG Grid drops .ag-row-hover once the pointer leaves the row for the
  // portaled menu, and renders one node per row in the pinned-left and center
  // containers. Latch the hover paint on both while the menu is open.
  useEffect(() => {
    const rowId = overflowAnchor?.closest(".ag-row")?.getAttribute("row-id");
    if (!rowId) return;
    const rows = document.querySelectorAll(
      `.ag-row[row-id="${CSS.escape(rowId)}"]`,
    );
    rows.forEach((row) => row.classList.add(ORDERED_ROW_MENU_OPEN_CLASS));
    return () => {
      rows.forEach((row) => row.classList.remove(ORDERED_ROW_MENU_OPEN_CLASS));
    };
  }, [overflowAnchor]);

  return (
    <NewReqNameAndActionsText>
      <NewReqNameWithStatus>
        {value}
        {isPhlebotomyDisabled && (
          <Tooltip
            arrow
            placement="top"
            title={
              <NewReqActionTooltip>Phlebotomy Requested</NewReqActionTooltip>
            }
          >
            <NewReqNameStatusIcon>
              <MobilePhlebotomyIcon color={theme.redMedium} />
            </NewReqNameStatusIcon>
          </Tooltip>
        )}
        {isShipKitDisabled && (
          <Tooltip
            arrow
            placement="top"
            title={
              <NewReqActionTooltip>Kit Shipped to Patient</NewReqActionTooltip>
            }
          >
            <NewReqNameStatusIcon>
              <FontAwesomeIcon
                icon={faBox}
                fontSize={14}
                color={theme.blueMedium}
              />
            </NewReqNameStatusIcon>
          </Tooltip>
        )}
      </NewReqNameWithStatus>
      {actionBarEnabled && (
        <OrderedRowActionBar
          orderId={data.unity_complete_id}
          directActions={directActions}
          overflowActions={overflowActions}
          hasOverflow={hasOverflow}
          overflowAnchor={overflowAnchor}
          onOpenOverflow={setOverflowAnchor}
          onCloseOverflow={() => setOverflowAnchor(null)}
        />
      )}
      {shipKitModalOpen && (
        <ShipKitToPatientModal
          open={shipKitModalOpen}
          orderData={data}
          onClose={() => setShipKitModalOpen(false)}
          onSuccess={() => node?.setData({ ...data, is_ship_to_patient: true })}
        />
      )}
    </NewReqNameAndActionsText>
  );
};
